import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AcceptInvitationPage({ user, onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [invitationInfo, setInvitationInfo] = useState(null);
  const { isDarkMode } = useTheme();

  // Fetch invitation info
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid invitation link.');
      return;
    }
    axios.get(`${API}/api/team/invitation-info?token=${token}`)
      .then(res => setInvitationInfo(res.data))
      .catch(() => {});
  }, [token]);

  const handleAccept = async () => {
    if (!user) {
      localStorage.setItem('pending_invitation_token', token);
      navigate(`/login?redirect=/accept-invitation?token=${token}`);
      return;
    }

    setStatus('loading');
    try {
      const authToken = localStorage.getItem('token');
      await axios.post(`${API}/api/team/accept-invitation`,
        { token },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setStatus('success');
      setMessage('You have successfully joined the team!');
      toast.success('Welcome to the team!');
      try {
        const authToken = localStorage.getItem('token');
        const meRes = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        if (meRes.data.plan_info) {
          localStorage.setItem('plan_info', JSON.stringify(meRes.data.plan_info));
        }
        if (meRes.data) {
          localStorage.setItem('user', JSON.stringify({
            id: meRes.data.id, email: meRes.data.email, name: meRes.data.name
          }));
        }
      } catch (e) { console.warn('Plan refresh failed', e); }
      localStorage.removeItem('pending_invitation_token');
      setTimeout(() => { window.location.href = '/dashboard'; }, 2000);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Failed to accept invitation.');
      toast.error(err.response?.data?.detail || 'Failed to accept invitation.');
    }
  };

  // Auto-accept if user is logged in and token exists
  useEffect(() => {
    if (user && token && status === 'idle') {
      handleAccept();
    }
  }, [user, token]);

  const textPrimary = isDarkMode ? '#f1f5f9' : '#1d1d1f';
  const textSecondary = isDarkMode ? '#94a3b8' : '#6e6e73';
  const cardBg = isDarkMode ? '#111827' : '#fff';
  const pageBg = isDarkMode ? '#0f172a' : '#f5f5f7';
  const iconCircleBg = isDarkMode ? '#1e3a5f' : '#e5f2ff';

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: pageBg, padding: '20px'
    }}>
      <div style={{
        background: cardBg, borderRadius: '20px', padding: '48px 40px',
        maxWidth: '440px', width: '100%', textAlign: 'center',
        boxShadow: isDarkMode ? '0 4px 32px rgba(0,0,0,0.4)' : '0 4px 32px rgba(0,0,0,0.08)'
      }}>
        {/* Icon */}
        <div style={{
          width: '72px', height: '72px', background: iconCircleBg, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px'
        }}>
          {status === 'success' ? <CheckCircle size={36} color="#34C759" /> :
           status === 'error' ? <XCircle size={36} color="#FF3B30" /> :
           status === 'loading' ? <Loader2 size={36} color="#2563eb" className="animate-spin" /> :
           <Users size={36} color="#2563eb" />}
        </div>

        {status === 'idle' && (
          <>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: textPrimary }}>
              Team Invitation
            </h2>
            {invitationInfo && (
              <p style={{ color: textSecondary, marginBottom: '8px' }}>
                <strong>{invitationInfo.inviter_name}</strong> has invited you to join{' '}
                <strong>{invitationInfo.team_name}</strong>
              </p>
            )}
            <p style={{ color: textSecondary, marginBottom: '32px', fontSize: '15px' }}>
              {user ? 'Click below to accept the invitation.' : 'Please log in to accept this invitation.'}
            </p>
            <button
              onClick={handleAccept}
              style={{
                width: '100%', padding: '14px', background: '#2563eb', color: '#fff',
                border: 'none', borderRadius: '12px', fontSize: '17px', fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {user ? 'Accept Invitation' : 'Log In to Accept'}
            </button>
          </>
        )}

        {status === 'loading' && (
          <>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: textPrimary }}>
              Accepting Invitation...
            </h2>
            <p style={{ color: textSecondary }}>Please wait a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: textPrimary }}>
              Welcome to the Team!
            </h2>
            <p style={{ color: textSecondary, marginBottom: '24px' }}>{message}</p>
            <p style={{ color: isDarkMode ? '#64748b' : '#86868b', fontSize: '13px' }}>Redirecting to dashboard...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: textPrimary }}>
              Invitation Error
            </h2>
            <p style={{ color: '#FF3B30', marginBottom: '24px' }}>{message}</p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                width: '100%', padding: '14px', background: '#2563eb', color: '#fff',
                border: 'none', borderRadius: '12px', fontSize: '17px', fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
